import { useNavigate, useSearchParams } from 'react-router';
import { List, useTable, CreateButton } from '@refinedev/antd';
import { useInvalidate } from '@refinedev/core';
import { Tag } from 'antd';
import type { User } from '@viq-admin/types';
import {
  DataTable,
  RowActionsMenu,
  roleLabel,
  useUrlTableState,
  type DataColumn,
  type RowAction,
  type TableState,
} from '@viq-admin/ui';
import { reactivateUser } from '../../api/users';
import { useCompanyScope } from '../../components/CompanyScopePicker';
import { CompanyLink } from '../../components/EntityLinks';
import { RoleTag } from '../../views/UserDetail/RoleTag';

/**
 * Cross-tenant user list. All companies at once, with a Company column that
 * links to the tenant's page (the backend join supplies `companyName`; a
 * missing one is filled from the tenant list, never shown as a bare id).
 * Active/Inactive is derived from `deactivatedAt`. Opening a user is the ROW
 * CLICK (→ `/users/:oid`, the user page) — see the DataTable spec; the
 * company link stops propagation, so clicking it opens the company instead.
 *
 * `read User` gates the whole page via the route's `RequireCan`; `manage
 * User` gates every per-row action, so `super_admin_readonly` sees no kebab
 * at all (`RowActionsMenu` renders nothing once every action is denied).
 */
type UserRow = User & { companyName?: string };

/**
 * Seeds the default sort when the URL carries none. Module-level so its
 * identity is stable across renders — see DataTable's `defaultState` JSDoc.
 */
const DEFAULT_STATE: Partial<TableState> = {
  sort: { field: 'name', order: 'ascend' },
};

export function UserList() {
  const { tableProps } = useTable<UserRow>({
    resource: 'users',
    // DataTable owns paging; Refine must not slice the payload.
    pagination: { mode: 'off' },
  });
  const invalidate = useInvalidate();
  const navigate = useNavigate();
  // `users` is scope-aware: switching the operate-as tenant refetches a
  // different (often smaller) set. Pass it as `resetKey` so the DataTable drops
  // its sticky rows on the switch and shows a fresh load instead of holding the
  // previous tenant's larger list and snapping to the new one.
  const { companyId: scope } = useCompanyScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useUrlTableState(searchParams, setSearchParams);

  const columns: DataColumn<UserRow>[] = [
    { dataIndex: 'name', key: 'name', title: 'Name', kind: 'text', width: 200 },
    { dataIndex: 'email', key: 'email', title: 'Email', kind: 'text', width: 260 },
    {
      dataIndex: 'companyName',
      key: 'companyName',
      title: 'Company',
      kind: 'text',
      width: 180,
      render: (_: unknown, r: UserRow) => <CompanyLink id={r.companyId} name={r.companyName} />,
      projectValue: (r) => r.companyName || r.companyId,
    },
    {
      dataIndex: 'role',
      key: 'role',
      title: 'Role',
      kind: 'enum',
      width: 140,
      render: (v: string) => <RoleTag role={v} />,
      projectValue: (r) => roleLabel(r.role),
    },
    {
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      title: 'Last login',
      kind: 'dateTime',
      width: 200,
    },
    {
      dataIndex: 'deactivatedAt',
      key: 'status',
      title: 'Status',
      kind: 'enum',
      width: 100,
      render: (v: string | null) =>
        v ? <Tag>Inactive</Tag> : <Tag color="green">Active</Tag>,
      projectValue: (r) => (r.deactivatedAt ? 'Inactive' : 'Active'),
    },
    {
      key: 'actions',
      title: '',
      kind: 'actions',
      render: (_: unknown, record: UserRow) => {
        // NOTE: no Edit here. RowActionsMenu is Edit/View-less by convention
        // (opening a record is the row click), and the user page now carries
        // its own Edit button in its header, so the exception this list used
        // to make is gone. An active user has no row action left; an
        // inactive one keeps Reactivate.
        const actions: RowAction[] = record.deactivatedAt
          ? [
              {
                key: 'reactivate',
                label: 'Reactivate user',
                cap: 'manage:User',
                onRun: async () => {
                  await reactivateUser(record.oid);
                  invalidate({ resource: 'users', invalidates: ['list'] });
                },
                confirm: {
                  title: `Reactivate ${record.name}?`,
                  content: `${record.name} will regain access immediately.`,
                  okText: 'Reactivate',
                  successMessage: `${record.name} reactivated.`,
                },
              },
            ]
          : [];
        return <RowActionsMenu actions={actions} />;
      },
    },
  ];

  return (
    <List headerButtons={[<CreateButton key="create" />]}>
      <DataTable<UserRow>
        tableProps={tableProps}
        columns={columns}
        rowKey="oid"
        storageKey="usersList"
        resetKey={scope}
        state={state}
        onStateChange={setState}
        defaultState={DEFAULT_STATE}
        searchFields={['name', 'email']}
        searchPlaceholder="Search users"
        onRowClick={(record) => navigate(`/users/${record.oid}`)}
        empty={{
          title: 'No users yet',
          description: 'Invite the first user to get started.',
        }}
      />
    </List>
  );
}
